<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Company</label>
          <div class="group-item">
            <label>Logo</label>
            <UploadImage
              :image="{ imageSrc: fields.logo }"
              :height="`120`"
              :typeUpload="'image'"
              @callback="obj => (fields.logo = obj.imageSrc)"
              @callbackDelete="fields.logo = ''"
            />
            <label class="mt-4 d-block">Description</label>
            <v-text-field v-model="fields.description" hide-details class="mt-0" />
          </div>
        </div>

        <div v-for="column in fields.columns" :key="`column-${column.key}`" class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">{{ column.title || column.key }} (max {{ MAX_LINK_ITEMS }})</label>
            <v-btn
              small
              color="primary"
              :disabled="column.items.length >= MAX_LINK_ITEMS"
              @click="handleAddLink(column)"
            >
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Link</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <label>Column title</label>
            <v-text-field v-model="column.title" hide-details class="mt-0" />
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in column.items" :key="`link-${column.key}-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>{{ item.label || `Link ${index + 1}` }}</span>
                    <v-btn icon @click.stop="handleRemoveLink(column, index)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label>Label</label>
                  <v-text-field v-model="item.label" hide-details class="mt-0 mb-3" />
                  <label>URL</label>
                  <v-text-field v-model="item.url" hide-details class="mt-0" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Contact</label>
          <div class="group-item">
            <label>Column title</label>
            <v-text-field v-model="fields.contactTitle" hide-details class="mt-0" />
          </div>
          <div v-for="(item, index) in fields.contactItems" :key="`contact-${index}`" class="group-item">
            <label>Label</label>
            <v-text-field v-model="item.label" hide-details class="mt-0 mb-3" />
            <label>Text</label>
            <v-text-field v-model="item.text" hide-details class="mt-0 mb-3" />
            <label>URL</label>
            <v-text-field v-model="item.url" hide-details class="mt-0" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Copyright</label>
          <div class="group-item">
            <v-text-field v-model="fields.copyright" hide-details class="mt-0" />
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';

const MAX_LINK_ITEMS = 8;

const getDefaultFields = () => ({
  title: 'Cefloreco Footer Section',
  settings: {
    paddingTop: false,
    paddingBottom: false,
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  logo: '',
  description: 'Digital entertainment, beautifully made.',
  columns: [
    { key: 'product', title: 'Our product', items: [] },
    { key: 'company', title: 'Company Info', items: [] },
    { key: 'legal', title: 'Legal', items: [] },
  ],
  contactTitle: 'Contact',
  contactItems: [],
  copyright: '© 2026 CEFLORE PTE. LTD',
});

export default {
  name: 'CeflorecoFooterSectionEditor',
  components: { UploadImage, SectionLayout },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fields: getDefaultFields(),
      MAX_LINK_ITEMS,
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      columns: extract.columns && extract.columns.length ? extract.columns : def.columns,
      contactItems: extract.contactItems || def.contactItems,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCeflorecoFooter(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddLink(column) {
      if (column.items.length >= MAX_LINK_ITEMS) return;
      column.items.push({ label: 'New link', url: '' });
    },
    handleRemoveLink(column, index) {
      column.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
